import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { IAnswer, ICreateSurvey, IQusteion, ISurvey, ISurveyStats } from 'src/utils/Interfaces/survey.interface'
import { IResponse } from 'src/utils/Interfaces/state.interface'

const BASE_URL = import.meta.env.VITE_BASE_URL
const BLOCK_URL = '/api/surveys'

export const surveysApi = createApi({
  reducerPath: 'surveysApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    credentials: 'include'
  }),
  tagTypes: ['Survey', 'Surveys', 'Client', 'Questions'],
  endpoints: (build) => ({
    getSurveys: build.query<ISurvey[], string>({
      query: (string) => ({
        url: `${BLOCK_URL}${string ? `?${string}` : ''}`
      }),
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Survey' as const, id })), { type: 'Surveys', id: 'LIST' }]
          : [{ type: 'Surveys', id: 'LIST' }]
    }),
    getSurvey: build.query<ISurvey, number>({
      query: (id) => ({
        url: `${BLOCK_URL}/${id}`
      }),
      providesTags: (_result, _error, id) => [{ type: 'Survey', id }]
    }),
    createSurvey: build.mutation<IResponse<ISurvey>, ICreateSurvey>({
      query: (body) => ({
        url: BLOCK_URL,
        method: 'POST',
        body
      }),
      invalidatesTags: [{ type: 'Surveys', id: 'LIST' }, 'Client']
    }),
    updateSurvey: build.mutation<IResponse<ISurvey>, Partial<ISurvey>>({
      query: (body) => ({
        url: `${BLOCK_URL}/${body.id}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Survey', id }, { type: 'Surveys', id: 'LIST' }, 'Client']
    }),
    deleteSurvey: build.mutation<IResponse<unknown>, number>({
      query: (id) => ({
        url: `${BLOCK_URL}/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [{ type: 'Surveys', id: 'LIST' }, 'Client']
    }),
    copySurvey: build.mutation<IResponse<ISurvey>, number>({
      query: (id) => ({
        url: `${BLOCK_URL}/copy/${id}`,
        method: 'POST'
      }),
      invalidatesTags: [{ type: 'Surveys', id: 'LIST' }, 'Client']
    }),
    getSurveyAnswers: build.query<IAnswer[], number>({
      query: (id) => ({
        url: `/api/answers/${id}`
      })
    }),
    getSurveyStats: build.query<ISurveyStats, number>({
      query: (id) => ({
        url: `/api/survey-events/stats/${id}`
      })
    }),
    getQuestions: build.query<IQusteion[], number>({
      query: (id) => ({
        url: `/api/questions?surveyId=${id}`
      }),
      providesTags: ['Questions']
    }),
    createQuestion: build.mutation<IQusteion, IQusteion>({
      query: (body) => ({
        url: `/api/questions`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['Questions']
    }),
    updateQuestion: build.mutation<IQusteion, IQusteion>({
      query: (body) => ({
        url: `/api/questions/${body.id}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: ['Questions']
    }),
    deleteQuestion: build.mutation<IResponse<unknown>, number>({
      query: (id) => ({
        url: `/api/questions/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Questions']
    })
  })
})

export const {
  useGetSurveysQuery,
  useLazyGetSurveysQuery,
  useGetSurveyQuery,
  useCreateSurveyMutation,
  useUpdateSurveyMutation,
  useDeleteSurveyMutation,
  useGetSurveyAnswersQuery,
  useGetSurveyStatsQuery,
  useCreateQuestionMutation,
  useUpdateQuestionMutation,
  useDeleteQuestionMutation,
  useGetQuestionsQuery
} = surveysApi
